// Chat store (Layer CH): what the Chat page renders. Lives OUTSIDE the React
// tree (§9.3.1) — chatClient writes here, components read snapshots. Only
// DECRYPTED, device-local facts pass through; nothing in this store ever
// came from the server in readable form.

import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

export interface ChatReactionView {
  emoji: string;
  count: number;
  mine: boolean;
}

export interface ChatFileView {
  objectKey: string;
  name: string;
  size: number;
  mime: string;
  nonce: string;
  key: string;
  hash: string;
}

export interface ChatMessageView {
  seq: number;
  isMine: boolean;
  text: string;
  at: number;
  reactions?: ChatReactionView[];
  file?: ChatFileView;
  /** Optimistic-send fields: a pending message has a clientId and no real
   * seq yet; status/progress drive the sending loader; localUrl is the local
   * image preview shown before the upload/download round-trip. */
  clientId?: string;
  status?: 'sending' | 'failed';
  progress?: number;
  localUrl?: string;
}

export interface ChatConvView {
  id: string;
  kind: 'dm' | 'group';
  peerUserId?: string;
  unread: number;
  lastText?: string;
  lastAt?: number;
  /** Still waiting for the other side's welcome (race on DM creation). */
  pending: boolean;
  removed: boolean;
}

export interface ChatView {
  /** Clock snapshot taken when the thread last changed — render never reads
   * Date.now() itself (React compiler purity). */
  clock: number;
  /** Device enrolled + session loaded — the page can work. */
  ready: boolean;
  /** Something broke badly enough that chat cannot run (wasm, storage). */
  failed: boolean;
  convs: ChatConvView[];
  /** The open thread, or null. */
  openConvId: string | null;
  messages: ChatMessageView[];
  totalUnread: number;
}

const initial: ChatView = {
  clock: 0,
  ready: false,
  failed: false,
  convs: [],
  openConvId: null,
  messages: [],
  totalUnread: 0,
};

export const chatStore = createStore<ChatView>(() => ({ ...initial }));

export function useChatStore<T>(selector: (s: ChatView) => T): T {
  return useStore(chatStore, selector);
}

export function setChatReady(ready: boolean, failed = false): void {
  chatStore.setState({ ready, failed });
}

export function setChatConvs(convs: ChatConvView[]): void {
  chatStore.setState({
    convs,
    totalUnread: convs.reduce((n, c) => n + c.unread, 0),
  });
}

export function setOpenConversation(convId: string | null, messages: ChatMessageView[]): void {
  chatStore.setState({ openConvId: convId, messages, clock: Date.now() });
}

export function updateOpenMessage(convId: string, seq: number, reactions: ChatReactionView[]): void {
  const s = chatStore.getState();
  if (s.openConvId !== convId) return;
  chatStore.setState({
    messages: s.messages.map((m) => (m.seq === seq ? { ...m, reactions } : m)),
    clock: Date.now(),
  });
}

export function appendOpenMessage(convId: string, message: ChatMessageView): void {
  const s = chatStore.getState();
  if (s.openConvId !== convId) return;
  chatStore.setState({ messages: [...s.messages, message], clock: Date.now() });
}

/** Update one open message identified by clientId (optimistic sends) or seq. */
export function patchOpenMessage(
  convId: string,
  key: { clientId?: string; seq?: number },
  patch: Partial<ChatMessageView>,
): void {
  const s = chatStore.getState();
  if (s.openConvId !== convId) return;
  const hit = (m: ChatMessageView) =>
    (key.clientId !== undefined && m.clientId === key.clientId) ||
    (key.seq !== undefined && m.seq === key.seq);
  chatStore.setState({
    messages: s.messages.map((m) => (hit(m) ? { ...m, ...patch } : m)),
    clock: Date.now(),
  });
}

export function resetChat(): void {
  chatStore.setState({ ...initial });
}
